import { useRef, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from '@/hooks/useDebounce';
import {
  ClipboardCheck,
  Download,
  FileSpreadsheet,
  FileText,
  Filter as FilterIcon,
  History,
  ImageIcon,
  Plus,
  Save,
  Search,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { buildErrorToast } from '@/lib/apiError';
import { usePermission } from '@/hooks/usePermission';
import {
  downloadUiAuditExport,
  downloadUiAuditSnapshot,
  type IUiAuditFilter,
  type IUiAuditScreen,
  type UiAuditCheckMethod,
  type UiAuditSeverity,
  type UiAuditStatus,
} from '@/api/ui-audit.api';
import { uploadFile as uploadToR2 } from '@/api/file.api';
import {
  useAddUiAuditAttachment,
  useCreateUiAuditRule,
  useCreateUiAuditSnapshot,
  useImportUiAuditAi,
  useRemoveUiAuditAttachment,
  useUiAuditCell,
  useUiAuditMatrix,
  useUiAuditSnapshots,
  useUpdateUiAuditCell,
} from '@/hooks/useUiAudit';

const STATUS_OPTIONS: { value: UiAuditStatus; label: string; cls: string }[] = [
  { value: 'PASS',              label: 'Pass',         cls: 'bg-green-100 text-green-800' },
  { value: 'FAIL',              label: 'Fail',         cls: 'bg-red-100 text-red-800' },
  { value: 'NA',                label: 'N/A',          cls: 'bg-gray-100 text-gray-600' },
  { value: 'PENDING',           label: 'Pending',      cls: 'bg-amber-100 text-amber-800' },
  { value: 'AI_SUGGESTED_PASS', label: 'AI? Pass',     cls: 'bg-violet-100 text-violet-800' },
  { value: 'AI_SUGGESTED_FAIL', label: 'AI? Fail',     cls: 'bg-pink-100 text-pink-800' },
];

const STATUS_BADGE: Record<UiAuditStatus, { label: string; cell: string }> = {
  PASS:              { label: '✓',  cell: 'bg-green-100 text-green-800 hover:bg-green-200' },
  FAIL:              { label: '✗',  cell: 'bg-red-100 text-red-800 hover:bg-red-200' },
  NA:                { label: '—',  cell: 'bg-gray-100 text-gray-500 hover:bg-gray-200' },
  PENDING:           { label: '·',  cell: 'bg-amber-50 text-amber-700 hover:bg-amber-100' },
  AI_SUGGESTED_PASS: { label: 'AP', cell: 'bg-violet-100 text-violet-800 hover:bg-violet-200' },
  AI_SUGGESTED_FAIL: { label: 'AF', cell: 'bg-pink-100 text-pink-800 hover:bg-pink-200' },
};

const SEVERITIES: UiAuditSeverity[] = ['LOW', 'MEDIUM', 'HIGH'];
const CHECK_METHODS: UiAuditCheckMethod[] = ['AI_STATIC', 'RUNTIME', 'MANUAL'];

const CANONICAL_SECTIONS = ['§1', '§2', '§3', '§4', '§5', '§6', '§7', '§8', '§9', '§10', '§11', '§12'] as const;

function fmtDateTime(s: string | null | undefined) {
  if (!s) return '—';
  const d = new Date(s);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function UiAuditPage() {
  const canViewSnapshots = usePermission('SY19', 'snapshots', 'VIEW');
  const [tab, setTab] = useState<'matrix' | 'snapshots'>('matrix');

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-2">
        <ClipboardCheck className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">UI Consistency Audit</h1>
          <p className="text-sm text-muted-foreground">
            Track UI-CONVENTIONS.md compliance across the Rule × Screen matrix.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as 'matrix' | 'snapshots')}>
        <TabsList>
          <TabsTrigger value="matrix">Matrix</TabsTrigger>
          {canViewSnapshots && <TabsTrigger value="snapshots">Snapshots</TabsTrigger>}
        </TabsList>
        <TabsContent value="matrix">
          <MatrixTab />
        </TabsContent>
        {canViewSnapshots && (
          <TabsContent value="snapshots">
            <SnapshotsTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

// ─── Matrix Tab ─────────────────────────────────────────────────────────────

function MatrixTab() {
  const [params, setParams] = useSearchParams();

  // Controlled local state for search — debounced before writing to URL (R-3.2: 300ms)
  const [searchInput, setSearchInput] = useState(params.get('q') ?? '');
  const debouncedSearch = useDebounce(searchInput, 300);

  useEffect(() => {
    const next = new URLSearchParams(params);
    if (debouncedSearch) next.set('q', debouncedSearch);
    else next.delete('q');
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const filter: IUiAuditFilter = useMemo(
    () => ({
      section: params.get('section') ?? undefined,
      screenCode: params.get('screen') ?? undefined,
      status: (params.get('status') as UiAuditStatus | null) ?? undefined,
      severity: (params.get('severity') as UiAuditSeverity | null) ?? undefined,
      q: params.get('q') ?? undefined,
    }),
    [params],
  );
  const updateFilter = (key: string, value: string | undefined) => {
    const next = new URLSearchParams(params);
    if (value && value !== 'ALL') next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };
  const hasFilter = Object.values(filter).some(Boolean);
  const clearFilters = () => {
    setSearchInput('');
    setParams({}, { replace: true });
  };

  const { data, isLoading } = useUiAuditMatrix(filter);
  const [selected, setSelected] = useState<{ ruleId: string; screenId: string } | null>(null);
  const [addRuleOpen, setAddRuleOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [snapOpen, setSnapOpen] = useState(false);
  const { toast } = useToast();
  const errToast = buildErrorToast(toast);

  const canCreate = usePermission('SY19', null, 'CREATE');
  const canUpdate = usePermission('SY19', null, 'UPDATE');
  const canExport = usePermission('SY19', null, 'EXPORT');

  // Cache full screen list — data?.screens shrinks when screenCode filter is active
  const allScreensRef = useRef<IUiAuditScreen[]>([]);
  if ((data?.screens ?? []).length > allScreensRef.current.length) {
    allScreensRef.current = data!.screens;
  }

  const onExport = async (fmt: 'excel' | 'pdf') => {
    try {
      await downloadUiAuditExport(fmt, filter);
    } catch (e) {
      errToast(e, 'Export failed');
    }
  };

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {(
          [
            { key: 'pass', label: 'Pass', cls: 'bg-green-50 text-green-700 border-green-200' },
            { key: 'fail', label: 'Fail', cls: 'bg-red-50 text-red-700 border-red-200' },
            { key: 'na', label: 'N/A', cls: 'bg-gray-50 text-gray-700 border-gray-200' },
            { key: 'pending', label: 'Pending', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
            { key: 'aiSuggested', label: 'AI Suggested', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
          ] as const
        ).map((s) => (
          <div key={s.key} className={`border rounded-md p-3 ${s.cls}`}>
            <div className="text-2xl font-bold">{data?.summary?.[s.key] ?? 0}</div>
            <div className="text-xs">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Header actions */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-muted-foreground space-y-0.5">
          {data ? (
            <>
              <div>
                {data.rules.length} rules × {data.screens.length} screens ={' '}
                <strong>{data.summary.total}</strong> cells
              </div>
              {data.lastUpdated && (
                <div className="text-xs">
                  Last updated: {fmtDateTime(data.lastUpdated.at)}
                </div>
              )}
            </>
          ) : (
            'Loading…'
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canUpdate && (
            <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}>
              <Upload className="h-4 w-4 mr-1" /> Import AI
            </Button>
          )}
          {canExport && (
            <>
              <Button variant="outline" size="sm" onClick={() => onExport('excel')}>
                <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel
              </Button>
              <Button variant="outline" size="sm" onClick={() => onExport('pdf')}>
                <FileText className="h-4 w-4 mr-1" /> PDF
              </Button>
              <Button variant="outline" size="sm" onClick={() => setSnapOpen(true)}>
                <Save className="h-4 w-4 mr-1" /> Save Snapshot
              </Button>
            </>
          )}
          {canCreate && (
            <Button size="sm" onClick={() => setAddRuleOpen(true)}>
              <Plus className="h-4 w-4 mr-1" /> Rule
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 p-3 bg-muted/30 rounded-md">
        <FilterIcon className="h-4 w-4 text-muted-foreground" />
        <Select value={filter.section ?? 'ALL'} onValueChange={(v) => updateFilter('section', v)}>
          <SelectTrigger className="w-32 h-8"><SelectValue placeholder="Section" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Sections</SelectItem>
            {CANONICAL_SECTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.screenCode ?? 'ALL'} onValueChange={(v) => updateFilter('screen', v)}>
          <SelectTrigger className="w-40 h-8"><SelectValue placeholder="Screen" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Screens</SelectItem>
            {allScreensRef.current.map((s) => (
              <SelectItem key={s.code} value={s.code}>{s.code} — {s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filter.status ?? 'ALL'} onValueChange={(v) => updateFilter('status', v)}>
          <SelectTrigger className="w-36 h-8"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Statuses</SelectItem>
            {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.severity ?? 'ALL'} onValueChange={(v) => updateFilter('severity', v)}>
          <SelectTrigger className="w-32 h-8"><SelectValue placeholder="Severity" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Severities</SelectItem>
            {SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search rule/screen…"
            className="h-8 pl-7 w-52"
          />
        </div>
        {hasFilter && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="h-3.5 w-3.5 mr-1" /> Clear Filters
          </Button>
        )}
      </div>

      {/* Matrix table */}
      <div className="border rounded-md overflow-auto max-h-[70vh] relative">
        {isLoading ? (
          <div className="p-8 text-center text-muted-foreground">Loading matrix…</div>
        ) : !data || data.rules.length === 0 || data.screens.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            {hasFilter ? 'No results match the current filters.' : 'No data.'}
          </div>
        ) : (
          <table className="text-xs border-collapse min-w-full">
            <thead className="sticky top-0 bg-background z-20">
              <tr>
                <th className="sticky left-0 z-30 bg-muted px-2 py-2 border text-center font-semibold min-w-[56px]">Section</th>
                <th className="sticky left-[56px] z-30 bg-muted px-3 py-2 border text-left font-semibold min-w-[100px]">Rule ID</th>
                <th className="sticky left-[156px] z-30 bg-muted px-3 py-2 border text-left font-semibold min-w-[260px]">Description</th>
                {data.screens.map((s) => (
                  <th key={s.id} className="px-2 py-2 border text-center font-semibold whitespace-nowrap" title={s.name}>
                    {s.code}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rules.map((r) => (
                <tr key={r.id} className="hover:bg-muted/30">
                  <td className="sticky left-0 z-10 bg-background border px-2 py-1.5 text-center text-muted-foreground font-medium whitespace-nowrap text-xs">
                    {r.section}
                  </td>
                  <td className="sticky left-[56px] z-10 bg-background border px-3 py-1.5 font-medium whitespace-nowrap">
                    {r.code}
                  </td>
                  <td className="sticky left-[156px] z-10 bg-background border px-3 py-1.5">
                    {r.description}
                  </td>
                  {data.screens.map((s) => {
                    const cell = data.cells.find((c) => c.ruleId === r.id && c.screenId === s.id);
                    const status = (cell?.status ?? 'PENDING') as UiAuditStatus;
                    const badge = STATUS_BADGE[status];
                    const isAiSuggested = status === 'AI_SUGGESTED_PASS' || status === 'AI_SUGGESTED_FAIL';
                    const lowConfidence = isAiSuggested && cell?.aiConfidence != null && cell.aiConfidence < 0.7;
                    return (
                      <td key={s.id} className="border p-0 text-center">
                        <button
                          type="button"
                          onClick={() => setSelected({ ruleId: r.id, screenId: s.id })}
                          className={`w-full h-full px-2 py-1.5 text-xs font-semibold ${badge.cell} transition-colors ${lowConfidence ? 'ring-2 ring-inset ring-yellow-400' : ''}`}
                          title={`${r.code} × ${s.code} — ${status}${lowConfidence ? ` (confidence ${((cell!.aiConfidence ?? 0) * 100).toFixed(0)}% — review required)` : ''}`}
                        >
                          {badge.label}
                          {lowConfidence && <span className="ml-0.5 text-yellow-600">!</span>}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Side panel */}
      {selected && (
        <CellSheet
          ruleId={selected.ruleId}
          screenId={selected.screenId}
          onClose={() => setSelected(null)}
          canEdit={canUpdate}
        />
      )}

      {/* Add Rule */}
      <AddRuleDialog open={addRuleOpen} onOpenChange={setAddRuleOpen} />

      {/* Import AI */}
      <ImportAiDialog open={importOpen} onOpenChange={setImportOpen} />

      {/* Save Snapshot */}
      <SaveSnapshotDialog open={snapOpen} onOpenChange={setSnapOpen} filter={filter} />
    </div>
  );
}

// ─── Cell Side Panel ────────────────────────────────────────────────────────

function CellSheet({
  ruleId,
  screenId,
  onClose,
  canEdit,
}: {
  ruleId: string;
  screenId: string;
  onClose: () => void;
  canEdit: boolean;
}) {
  const { data, isLoading } = useUiAuditCell(ruleId, screenId);
  const update = useUpdateUiAuditCell();
  const addAtt = useAddUiAuditAttachment();
  const removeAtt = useRemoveUiAuditAttachment();
  const fileRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const errToast = buildErrorToast(toast);

  const [status, setStatus] = useState<UiAuditStatus | null>(null);
  const [severity, setSeverity] = useState<UiAuditSeverity | null>(null);
  const [notes, setNotes] = useState<string>('');
  const [notesError, setNotesError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const effectiveStatus = status ?? data?.status ?? 'PENDING';
  const effectiveSeverity = severity ?? data?.severity ?? 'MEDIUM';
  const effectiveNotes = notes !== '' ? notes : (data?.notes ?? '');

  const onSave = async () => {
    if (effectiveStatus === 'FAIL' && !effectiveNotes.trim()) {
      setNotesError('Notes are required when Fail — include code location (file:line)');
      return;
    }
    setNotesError(null);
    try {
      await update.mutateAsync({
        ruleId,
        screenId,
        dto: { status: effectiveStatus, severity: effectiveSeverity, notes: effectiveNotes || undefined },
      });
      toast({ title: 'Cell updated', description: 'Status saved successfully.' });
      onClose();
    } catch (e) {
      errToast(e, 'Update failed');
    }
  };

  const onUpload = async (file: File) => {
    try {
      setIsUploading(true);
      const fileUrl = await uploadToR2(file, 'ui-audit-attachments');
      await addAtt.mutateAsync({
        ruleId,
        screenId,
        dto: { fileUrl, fileName: file.name, fileSize: file.size, mimeType: file.type },
      });
    } catch (e) {
      errToast(e, 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const onRemoveAtt = async (id: string) => {
    try {
      await removeAtt.mutateAsync(id);
    } catch (e) {
      errToast(e, 'Remove failed');
    }
  };

  return (
    <>
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-[480px] sm:max-w-[480px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            {data?.rule.code} × {data?.screen.code} — {data?.screen.name}
          </SheetTitle>
          <SheetDescription>
            <span className="text-muted-foreground">{data?.rule.section}</span> — {data?.rule.description}
          </SheetDescription>
        </SheetHeader>

        {isLoading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : (
          <div className="space-y-4 mt-4">
            {/* Status */}
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Status</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {STATUS_OPTIONS.filter((s) => s.value !== 'AI_SUGGESTED_PASS' && s.value !== 'AI_SUGGESTED_FAIL').map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    disabled={!canEdit}
                    onClick={() => { setStatus(opt.value); if (opt.value !== 'FAIL') setNotesError(null); }}
                    className={`px-3 py-2 rounded-md text-xs font-medium border transition-colors ${
                      effectiveStatus === opt.value ? opt.cls + ' border-current' : 'bg-background hover:bg-muted'
                    } disabled:opacity-50`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {(effectiveStatus === 'AI_SUGGESTED_PASS' || effectiveStatus === 'AI_SUGGESTED_FAIL') && (
                <div className="mt-2 px-3 py-2 bg-violet-50 text-violet-800 text-xs rounded-md border border-violet-200">
                  AI suggested — confirm by selecting Pass or Fail above.
                  {data?.aiConfidence != null && (
                    <span className={`ml-2 font-semibold ${data.aiConfidence < 0.7 ? 'text-yellow-700' : 'text-violet-700'}`}>
                      Confidence: {(data.aiConfidence * 100).toFixed(0)}%{data.aiConfidence < 0.7 ? ' ⚠ review required' : ''}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Severity */}
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Severity</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {SEVERITIES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    disabled={!canEdit}
                    onClick={() => setSeverity(s)}
                    className={`px-3 py-2 rounded-md text-xs font-medium border transition-colors ${
                      effectiveSeverity === s ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted'
                    } disabled:opacity-50`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="cell-notes" className="text-xs uppercase tracking-wide text-muted-foreground">
                Notes{effectiveStatus === 'FAIL' && <span className="text-red-500 ml-1">*</span>}
              </Label>
              <Textarea
                id="cell-notes"
                value={effectiveNotes}
                onChange={(e) => { setNotes(e.target.value); if (e.target.value.trim()) setNotesError(null); }}
                placeholder="e.g. DEV/apps/web/src/pages/EmployeeList.tsx:142 — Department dropdown has 12 options without search"
                rows={5}
                disabled={!canEdit}
                className={`mt-1 font-mono text-xs ${notesError ? 'border-red-500' : ''}`}
              />
              {notesError && <p className="text-xs text-red-500 mt-1">{notesError}</p>}
            </div>

            {/* AI Evidence */}
            {data?.aiEvidence && (
              <div className="border rounded-md p-3 bg-violet-50/40">
                <div className="text-xs font-semibold text-violet-900 mb-1">
                  AI Evidence ({data.aiModel ?? '—'})
                  {data.aiConfidence != null && (
                    <span className={`ml-2 text-[11px] ${data.aiConfidence < 0.7 ? 'text-yellow-700' : 'text-violet-700'}`}>
                      {(data.aiConfidence * 100).toFixed(0)}% confidence
                    </span>
                  )}
                </div>
                <pre className="text-[11px] text-violet-900 whitespace-pre-wrap font-mono">{data.aiEvidence}</pre>
                <div className="text-[11px] text-violet-700 mt-1">Ran {fmtDateTime(data.aiRunAt)}</div>
              </div>
            )}

            {/* Screenshots */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <ImageIcon className="h-3.5 w-3.5" /> Screenshots
              </Label>
              {canEdit && (
                <div className="flex items-center gap-2">
                  <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-md border border-dashed border-input text-sm cursor-pointer hover:border-primary hover:text-primary transition-colors ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                    {isUploading ? (
                      <><span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" /> Uploading…</>
                    ) : (
                      <><Upload className="h-4 w-4" /> Choose Image</>
                    )}
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={isUploading}
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) { onUpload(f); e.target.value = ''; } }}
                    />
                  </label>
                  <span className="text-xs text-muted-foreground">PNG / JPG / GIF — max 10MB</span>
                </div>
              )}
              {data && data.attachments.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {data.attachments.map((att) => (
                    <div key={att.id} className="relative group border rounded overflow-hidden w-20 h-20 bg-muted flex items-center justify-center">
                      <img
                        src={att.fileUrl}
                        alt={att.fileName}
                        className="object-cover w-full h-full cursor-zoom-in"
                        title={att.fileName}
                        onClick={() => setPreviewUrl(att.fileUrl)}
                      />
                      {canEdit && (
                        <button
                          type="button"
                          onClick={() => onRemoveAtt(att.id)}
                          className="absolute top-0.5 right-0.5 hidden group-hover:flex items-center justify-center bg-black/60 text-white rounded w-5 h-5"
                          title="Remove"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* History */}
            <div>
              <div className="flex items-center gap-1 text-xs uppercase tracking-wide text-muted-foreground mb-1">
                <History className="h-3 w-3" /> History
              </div>
              {data && data.history.length > 0 ? (
                <ul className="space-y-1.5 max-h-48 overflow-y-auto border rounded-md p-2">
                  {data.history.map((h) => (
                    <li key={h.id} className="text-xs">
                      <span className="text-muted-foreground">{fmtDateTime(h.changedAt)}</span> ·{' '}
                      <strong>{h.changedBy}</strong> · {h.oldStatus ?? '—'} → {h.newStatus}
                      {h.note && <div className="text-[11px] text-muted-foreground ml-2">{h.note}</div>}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-xs text-muted-foreground">No history yet.</div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              {canEdit && (
                <Button onClick={onSave} disabled={update.isPending}>
                  {update.isPending ? 'Saving…' : 'Save'}
                </Button>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>

    {/* Lightbox */}
    {previewUrl && (
      <div
        className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80"
        onClick={() => setPreviewUrl(null)}
      >
        <button
          type="button"
          onClick={() => setPreviewUrl(null)}
          className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-1.5 hover:bg-black/80"
        >
          <X className="h-5 w-5" />
        </button>
        <img
          src={previewUrl}
          alt="Preview"
          className="max-w-[90vw] max-h-[90vh] rounded shadow-xl object-contain"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    )}
    </>
  );
}

// ─── Add Rule Dialog ────────────────────────────────────────────────────────

function AddRuleDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const create = useCreateUiAuditRule();
  const { toast } = useToast();
  const errToast = buildErrorToast(toast);
  const [form, setForm] = useState({
    code: '',
    section: '§1' as string,
    description: '',
    defaultSeverity: 'MEDIUM' as UiAuditSeverity,
    checkMethod: 'AI_STATIC' as UiAuditCheckMethod,
  });

  const submit = async () => {
    if (!form.description.trim()) return;
    try {
      await create.mutateAsync({
        code: form.code.trim() || undefined,
        section: form.section,
        description: form.description.trim(),
        defaultSeverity: form.defaultSeverity,
        checkMethod: form.checkMethod,
      });
      toast({ title: 'Rule created', description: 'Rule has been added successfully.' });
      onOpenChange(false);
      setForm({ ...form, code: '', description: '' });
    } catch (e) {
      errToast(e, 'Create rule failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Rule</DialogTitle>
          <DialogDescription>Add a new rule to the catalog (creates PENDING cells for every screen).</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 px-3">
          <div className="space-y-1.5">
            <Label htmlFor="rule-code">Code <span className="text-muted-foreground text-xs">(optional)</span></Label>
            <Input id="rule-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="R-CUSTOM-…" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rule-section">Section <span className="text-red-500">*</span></Label>
            <Select value={form.section} onValueChange={(v) => setForm({ ...form, section: v })}>
              <SelectTrigger id="rule-section"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CANONICAL_SECTIONS.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rule-desc">Description <span className="text-red-500">*</span></Label>
            <Textarea id="rule-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Default Severity</Label>
              <Select value={form.defaultSeverity} onValueChange={(v) => setForm({ ...form, defaultSeverity: v as UiAuditSeverity })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Check Method</Label>
              <Select value={form.checkMethod} onValueChange={(v) => setForm({ ...form, checkMethod: v as UiAuditCheckMethod })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CHECK_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={create.isPending || !form.description.trim()}>
            {create.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Import AI Dialog ───────────────────────────────────────────────────────

function ImportAiDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const importMut = useImportUiAuditAi();
  const { toast } = useToast();
  const errToast = buildErrorToast(toast);
  const [aiModel, setAiModel] = useState('claude-opus-4-6');
  const [jsonText, setJsonText] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [preview, setPreview] = useState<unknown[] | null>(null);

  const handleParse = (text: string) => {
    setJsonText(text);
    if (!text.trim()) {
      setParseError(null);
      setPreview(null);
      return;
    }
    try {
      const parsed = JSON.parse(text);
      const arr = Array.isArray(parsed) ? parsed : parsed?.cells;
      if (!Array.isArray(arr)) throw new Error('Expected array of cells (or {cells: [...]})');
      setParseError(null);
      setPreview(arr);
    } catch (e) {
      setParseError(`Invalid JSON: ${(e as Error).message}`);
      setPreview(null);
    }
  };

  const handleFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => handleParse(String(reader.result ?? ''));
    reader.readAsText(file);
  };

  const submit = async () => {
    if (!preview) return;
    try {
      const result = await importMut.mutateAsync({
        aiModel,
        aiRunAt: new Date().toISOString(),
        cells: preview as IImportAiCell[],
      });
      toast({
        title: 'AI import done',
        description: `Imported ${result.imported}/${result.total}. Overrode ${result.overridden} confirmed. ${result.errors.length} errors.`,
      });
      if (result.errors.length === 0) {
        onOpenChange(false);
        setJsonText('');
        setPreview(null);
      }
    } catch (e) {
      errToast(e, 'Import failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import AI Suggestions</DialogTitle>
          <DialogDescription>
            Upload JSON output from a local AI run (each cell: {`{ruleCode, screenCode, status: "Pass"|"Fail"|"NA", evidence?, fileLine?}`}).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 px-3">
          <div className="space-y-1.5">
            <Label htmlFor="ai-model">AI Model</Label>
            <Input id="ai-model" value={aiModel} onChange={(e) => setAiModel(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Upload file</Label>
            <Input type="file" accept="application/json,.json" onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
            }} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ai-json">Or paste JSON</Label>
            <Textarea
              id="ai-json"
              value={jsonText}
              onChange={(e) => handleParse(e.target.value)}
              rows={6}
              placeholder='[{"ruleCode": "R-11.X-a", "screenCode": "S09", "status": "Fail", "evidence": "12 options no search", "fileLine": "EmployeeList.tsx:142"}]'
              className="font-mono text-xs"
            />
          </div>
          {parseError && <div className="text-xs text-red-600">{parseError}</div>}
          {preview && (
            <div className="text-xs text-muted-foreground">
              Will import <strong>{preview.length}</strong> cells with model <strong>{aiModel}</strong>.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!preview || importMut.isPending}>
            {importMut.isPending ? 'Importing…' : 'Import'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type IImportAiCell = {
  ruleCode: string;
  screenCode: string;
  status: 'Pass' | 'Fail' | 'NA';
  confidence?: number;
  evidence?: string;
  fileLine?: string;
};

// ─── Save Snapshot Dialog ───────────────────────────────────────────────────

function SaveSnapshotDialog({
  open,
  onOpenChange,
  filter,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  filter: IUiAuditFilter;
}) {
  const create = useCreateUiAuditSnapshot();
  const { toast } = useToast();
  const errToast = buildErrorToast(toast);
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');

  const submit = async () => {
    if (!name.trim()) return;
    try {
      await create.mutateAsync({ dto: { snapshotName: name.trim(), description: desc.trim() || undefined }, filter });
      toast({ title: 'Snapshot saved', description: `Snapshot "${name}" has been created.` });
      onOpenChange(false);
      setName('');
      setDesc('');
    } catch (e) {
      errToast(e, 'Save snapshot failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save Snapshot</DialogTitle>
          <DialogDescription>
            Capture the current audit state — generates Excel + PDF with summary for future reference (immutable).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 px-3">
          <div className="space-y-1.5">
            <Label htmlFor="snap-name">Snapshot Name <span className="text-red-500">*</span></Label>
            <Input id="snap-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Sprint 5 closure" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="snap-desc">Description</Label>
            <Textarea id="snap-desc" value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!name.trim() || create.isPending}>
            {create.isPending ? 'Saving…' : 'Save Snapshot'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Snapshots Tab ──────────────────────────────────────────────────────────

function SnapshotsTab() {
  const { data, isLoading } = useUiAuditSnapshots();
  const { toast } = useToast();
  const errToast = buildErrorToast(toast);

  const download = async (id: string, fmt: 'excel' | 'pdf') => {
    try {
      await downloadUiAuditSnapshot(id, fmt);
    } catch (e) {
      errToast(e, 'Download failed');
    }
  };

  if (isLoading) return <div className="p-8 text-center text-muted-foreground">Loading…</div>;
  if (!data || data.length === 0) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        No snapshots yet. Create one from the Matrix tab → Save Snapshot.
      </div>
    );
  }

  return (
    <div className="border rounded-md overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-muted">
          <tr>
            <th className="px-3 py-2 text-left font-semibold">Name</th>
            <th className="px-3 py-2 text-left font-semibold">Description</th>
            <th className="px-3 py-2 text-left font-semibold">Exported</th>
            <th className="px-3 py-2 text-left font-semibold">By</th>
            <th className="px-3 py-2 text-center font-semibold">Pass / Fail / NA / Pending</th>
            <th className="px-3 py-2 text-right font-semibold">Download</th>
          </tr>
        </thead>
        <tbody>
          {data.map((snap) => (
            <tr key={snap.id} className="border-t hover:bg-muted/30">
              <td className="px-3 py-2 font-medium">{snap.snapshotName}</td>
              <td className="px-3 py-2 text-muted-foreground">{snap.description ?? '—'}</td>
              <td className="px-3 py-2">{fmtDateTime(snap.exportedAt)}</td>
              <td className="px-3 py-2">{snap.exportedBy}</td>
              <td className="px-3 py-2 text-center text-xs font-mono">
                <span className="text-green-700">{snap.summaryJson.pass}</span> /{' '}
                <span className="text-red-700">{snap.summaryJson.fail}</span> /{' '}
                <span className="text-gray-600">{snap.summaryJson.na}</span> /{' '}
                <span className="text-amber-700">{snap.summaryJson.pending}</span>
              </td>
              <td className="px-3 py-2 text-right">
                <Button variant="ghost" size="sm" onClick={() => download(snap.id, 'excel')}>
                  <Download className="h-3.5 w-3.5 mr-1" /> Excel
                </Button>
                <Button variant="ghost" size="sm" onClick={() => download(snap.id, 'pdf')}>
                  <Download className="h-3.5 w-3.5 mr-1" /> PDF
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
